import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { authInterceptor, httpErrorInterceptor, provideAuth } from '@angular-monorepo/auth';
import { API_URL, GlobalErrorHandler } from '@angular-monorepo/data-access';
import { MessageService } from 'primeng/api';
import { environment } from '../environments/environment';
import { ConfigService } from '../environments/environment.service';

export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const MarautoPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
    },
    primary: {
      50: '#fff4ed',
      100: '#ffe6d5',
      200: '#fec9aa',
      300: '#fda474',
      400: '#fb6b31',
      500: '#f94e16',
      600: '#ea340c',
      700: '#c2230c',
      800: '#9a1e12',
      900: '#7c1c12',
      950: '#430a07',
    },
  },
});

export const MarautoAppConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes, withEnabledBlockingInitialNavigation()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MarautoPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
          cssLayer: false,
        },
      },
    }),
    provideAuth(),
    provideHttpClient(withInterceptors([authInterceptor, httpErrorInterceptor])),

    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },

    MessageService,
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),

    {
      provide: 'AUTH_CONFIG',
      useValue: {
        apiUrl: environment.apiUrl,
        loginEndpoint: '/api/auth/login/',
        enableOwnRolesRequest: false,
        postLoginRoute: '/dashboard',
      },
    },
    {
      provide: API_URL,
      useFactory: (configService: ConfigService) => configService.getApiUrl(),
      deps: [ConfigService],
    },
  ],
};
